import { BadgeCheck, Clock, HandHeart, Heart, KeyRound, Languages, Smile } from "lucide-react";
import { IconCard, Notice, PageHero, SectionHead } from "../components/site/Blocks";
import { CaregiverApplication } from "../components/forms/CaregiverApplication";

const QUALITIES = [
  [Heart, "Compassion", "A genuine commitment to treating people with warmth, patience and dignity."],
  [Smile, "Reliability", "Being on time, prepared and consistent, so clients and families can count on you."],
  [Languages, "Communication", "Clear, respectful communication with clients, families and colleagues. Additional languages are valued."],
  [Clock, "Flexibility", "Openness to a range of schedules, including part-time, weekend or overnight work."],
];

export default function Careers() {
  return (
    <>
      <PageHero eyebrow="Careers · Join Our Care Team" title="Make a Difference in Someone’s Everyday Life." image="/images/careers.jpg" alt="A smiling caregiver talking with an older adult" testId="careers-hero"
        actions={<a href="#caregiver-application" className="btn-pill btn-primary" data-testid="careers-apply-cta-button">Apply to join our team</a>}>
        <p>We are always interested in hearing from caring, dependable people who want to support others with dignity and respect. If you are interested in caregiving and personal-support work, we invite you to share your information with us.</p>
      </PageHero>

      <section className="container-site py-16 sm:py-24">
        <SectionHead eyebrow="What we look for" title="Experience matters, and so does character.">
          Every member of our care team holds a recognized certification. Alongside that, we look for people who bring:
        </SectionHead>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {QUALITIES.map(([i, t, d]) => <IconCard key={t} icon={i} title={t}>{d}</IconCard>)}
        </div>
      </section>

      <section className="bg-[#F3EFEA] border-y border-[#E5E0D8]">
        <div className="container-site py-16 sm:py-20">
          <SectionHead eyebrow="Roles we hire for" title="Certified caregivers only.">
            To keep our clients safe, we only hire certified caregivers.
          </SectionHead>
          <div className="grid md:grid-cols-2 gap-6 mt-10" data-testid="careers-role-types">
            <div className="card-warm p-7">
              <HandHeart className="h-6 w-6 text-[#C86D51]" aria-hidden="true" />
              <h3 className="font-display text-xl font-semibold mt-4">Personal Support Workers &amp; Caregivers</h3>
              <p className="text-sm text-[#5A6561] mt-3 leading-relaxed">For our in-home care service. You will need a Personal Support Worker (PSW) certificate or an equivalent designation accepted for in-home care in Ontario.</p>
            </div>
            <div className="card-warm p-7">
              <KeyRound className="h-6 w-6 text-[#1E3A2B]" aria-hidden="true" />
              <h3 className="font-display text-xl font-semibold mt-4">Residential Support Workers</h3>
              <p className="text-sm text-[#5A6561] mt-3 leading-relaxed">For our independent living program. You will need a relevant certificate, such as PSW, Developmental Services Worker (DSW), Child and Youth Worker (CYW) or Social Service Worker (SSW).</p>
            </div>
          </div>
          <p className="text-xs text-[#5A6561] mt-6 flex items-center gap-1.5"><BadgeCheck className="h-4 w-4" /> Certificates and a Vulnerable Sector Check are verified before any offer is made.</p>
        </div>
      </section>

      <section id="caregiver-application" className="container-site py-16 sm:py-24 scroll-mt-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 space-y-6">
          <SectionHead eyebrow="Caregiver application" title="Share your information with us.">
            The application takes about ten minutes. You can attach a resume in PDF, DOC or DOCX format.
          </SectionHead>
          <Notice testId="careers-application-notice">We accept applications on an ongoing basis and keep them on file for upcoming opportunities. Submitting an application does not guarantee an interview or a position.</Notice>
        </div>
        <div className="lg:col-span-8"><CaregiverApplication /></div>
      </section>
    </>
  );
}
